// @ts-strict-ignore
import React, { useMemo } from 'react';
import { Text } from '@usealloy/component-library';
import {
  selectDataRoutes,
  selectDeactivatedLegacyDataRoutes,
  selectDiffMode,
  selectDynamicDataRoutingEnabled,
  selectFocusedAction,
  selectServiceRoutingCategoryNameInEditing,
  selectServices,
} from 'flow/store/redux/selectors';

import { useAppSelector } from '~/store/reduxHooks';
import {
  StyledDataRoutingColumns,
  StyledDataRoutingFormContainer,
  StyledLeftColumnContainer,
  StyledRightColumnContainer,
  StyledSpacer,
} from './styles';
import {
  getIncomingDataRoutingCategories,
  getOutgoingDataRoutingCategories,
  hasLegacyDataRoutesConfigured,
  isNodeOnRouteSide,
} from '~/dashboard/views/workflows/service/dataRoutes';
import type { DataRoutingCategories } from '~/dashboard/views/workflows/service/dataRoutes/types';
import type { Category, DataRoute } from '~/dashboard/views/workflows/store/engine/types';
import RoutingCategory from './RoutingCategory';
import DynamicRoutes from './DynamicRoutes';

const MODES = ['incoming', 'outgoing'] as const;

const COLUMN_COPY = {
  incoming: {
    Container: StyledLeftColumnContainer,
    header: 'Incoming',
    emptyState: 'There are currently no services in the Workflow which are eligible to '
      + 'route data from.',
  },
  outgoing: {
    Container: StyledRightColumnContainer,
    header: 'Outgoing',
    emptyState: 'There are currently no services in the Workflow which are eligible to '
      + 'route data to.',
  },
};

interface LegacyColumnProps {
  mode: 'incoming' | 'outgoing';
  isDynamicDataRoutingEnabled: boolean;
  deactivatedRoutes: { category: Category; dataRoute: DataRoute }[];
  displayedCategories: DataRoutingCategories[];
  availableCategories: DataRoutingCategories[];
  serviceCategoryNameInEdition: string;
  isDisabled?: boolean;
}

function LegacyColumn({
  mode,
  isDynamicDataRoutingEnabled,
  deactivatedRoutes,
  displayedCategories,
  availableCategories,
  serviceCategoryNameInEdition,
  isDisabled = false,
}: LegacyColumnProps) {
  const { Container, header, emptyState } = COLUMN_COPY[mode];

  return (
    <Container data-testid={`${mode}-column`}>
      {!isDynamicDataRoutingEnabled && <Text as="h3" size={3} weight={400}>{header}</Text>}
      {deactivatedRoutes.map(({ category, dataRoute }) => (
        <RoutingCategory
          key={dataRoute.publicId}
          mode={mode}
          dataRoutingCategory={{ category, dataRoute }}
          isEditing={false}
          isDynamicDataRoutingEnabled={isDynamicDataRoutingEnabled}
          isDeactivated
          isDisabled={isDisabled}
        />
      ))}
      {displayedCategories?.map(dataRoutingCategory => (
        <RoutingCategory
          key={dataRoutingCategory.category.category_id}
          mode={mode}
          dataRoutingCategory={dataRoutingCategory}
          isEditing={serviceCategoryNameInEdition === dataRoutingCategory.category.name}
          isDynamicDataRoutingEnabled={isDynamicDataRoutingEnabled}
          isDisabled={isDisabled}
        />
      ))}
      {!isDynamicDataRoutingEnabled && !availableCategories?.length && (
        <Text as="span" size={2} weight={400}>{emptyState}</Text>
      )}
    </Container>
  );
}

export default function DataRoutingTab() {
  const dataRoutes = useAppSelector(selectDataRoutes);
  const services = useAppSelector(selectServices);
  const isInDiffMode = useAppSelector(selectDiffMode);

  const serviceCategoryNameInEdition = useAppSelector(selectServiceRoutingCategoryNameInEditing);
  const focusedAction = useAppSelector(selectFocusedAction);
  const isDynamicDataRoutingEnabled = useAppSelector(selectDynamicDataRoutingEnabled);
  const deactivatedLegacyDataRoutes = useAppSelector(selectDeactivatedLegacyDataRoutes);

  const availableCategoriesByMode = useMemo(() => ({
    incoming: getIncomingDataRoutingCategories({ action: focusedAction, dataRoutes, services }),
    outgoing: getOutgoingDataRoutingCategories({ action: focusedAction, dataRoutes, services }),
  }), [focusedAction, dataRoutes, services]);

  const hasActiveLegacyDataRoutes = hasLegacyDataRoutesConfigured({
    incomingCategories: availableCategoriesByMode.incoming,
    outgoingCategories: availableCategoriesByMode.outgoing,
  });

  const legacyColumns = MODES.map(mode => {
    const availableCategories = availableCategoriesByMode[mode];
    // Once dynamic routing is on, only show categories that still need migrating: no route means
    // nothing to deactivate, and a backfilled route is the same row DynamicRoutes already renders.
    const displayedCategories = isDynamicDataRoutingEnabled
      ? availableCategories?.filter(({ dataRoute }) => dataRoute && !dataRoute.target_payload_key)
      : availableCategories;
    // deactivatedLegacyDataRoutes spans all nodes; keep only this node's, on this side.
    const deactivatedRoutes = deactivatedLegacyDataRoutes.filter(route => (
      route.mode === mode
      && isNodeOnRouteSide({ mode, dataRoute: route.dataRoute, actionId: focusedAction?.publicId })
    ));
    const isVisible = !isDynamicDataRoutingEnabled
      || deactivatedRoutes.length > 0
      || displayedCategories?.length > 0;
    return {
      mode, availableCategories, displayedCategories, deactivatedRoutes, isVisible,
    };
  });

  return (
    <StyledDataRoutingFormContainer>
      {legacyColumns.some(({ isVisible }) => isVisible) && (
        <StyledDataRoutingColumns data-testid="legacy-columns">
          {legacyColumns.map(column => column.isVisible && (
            <LegacyColumn
              key={column.mode}
              mode={column.mode}
              isDynamicDataRoutingEnabled={isDynamicDataRoutingEnabled}
              deactivatedRoutes={column.deactivatedRoutes}
              displayedCategories={column.displayedCategories}
              availableCategories={column.availableCategories}
              serviceCategoryNameInEdition={serviceCategoryNameInEdition}
              isDisabled={isInDiffMode}
            />
          ))}
          <StyledSpacer />
        </StyledDataRoutingColumns>
      )}
      {isDynamicDataRoutingEnabled && (
        <DynamicRoutes
          hasActiveLegacyDataRoutes={hasActiveLegacyDataRoutes}
          isDisabled={isInDiffMode}
        />
      )}
    </StyledDataRoutingFormContainer>
  );
}
